import { createStyles, Skeleton } from '@mantine/core';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import repeat from '~/common/utils/repeat';
import useWindowGridVirtualizer from '~/domains/common/hooks/useWindowGridVirtualizer';
import dt from '~/testing';

import { PlayerCell, type PlayerCellProps } from '../PlayerCell/PlayerCell';

const useStyles = createStyles((theme) => ({
  wrapper: {
    padding: `${theme.other.spacing._16}px 0`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
    flexGrow: 1,
  },
  loadingContainer: {
    display: 'grid',
    gap: theme.other.spacing._8,
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  },
  row: {
    display: 'flex',
    gap: theme.other.spacing._8,
  },
  picksPanel: {
    position: 'sticky',
    bottom: theme.other.spacing._16,

    [theme.fn.largerThan(theme.other.layout.maxWidth + theme.other.layout.paddingLg * 2)]: {
      marginLeft: -theme.other.spacing._16,
      marginRight: -theme.other.spacing._16,
    },
  },
  listContainer: {
    flexGrow: 1,
  },
  emptyScreenContainer: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

function BoardPropsList({
  items,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isFetching,
  isLoading,
  noResults,
}: {
  items: PlayerCellProps[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  isLoading: boolean;
  noResults: JSX.Element;
}) {
  const { classes, theme } = useStyles();

  const { virtualizer, itemWidth, itemsPerRow, listRef, rowsCount } = useWindowGridVirtualizer({
    gap: theme.other.spacing._8,
    minColumnWidth: 200,
    itemsLength: items.length,
    hasNextPage,
    maxColumns: 6,
  });

  const virtualRows = virtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = virtualRows.slice(-1);

    if (!lastItem) return;

    if (lastItem.index >= rowsCount - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [rowsCount, fetchNextPage, virtualRows, hasNextPage, isFetchingNextPage]);

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.loadingContainer}>
          {repeat(8, (i) => (
            <Skeleton key={i} height={204} style={{ borderRadius: theme.other.spacing._8 }} />
          ))}
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div
        data-test-id={dt.contest.quickPicks.detail.general.emptyState}
        className={classes.emptyScreenContainer}
      >
        {noResults}
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      style={{
        height: `${virtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
      className={classes.listContainer}
    >
      {virtualRows.map((row) => {
        const isLoader = row.index === rowsCount;
        const rowItems = items.slice(row.index * itemsPerRow, (row.index + 1) * itemsPerRow);

        // eslint-disable-next-line react/no-unstable-nested-components
        function Wrapper({ children }: PropsWithChildren) {
          return (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${row.start - virtualizer.options.scrollMargin}px)`,
              }}
              data-index={row.index}
              ref={virtualizer.measureElement}
              className={classes.row}
            >
              {children}
            </div>
          );
        }

        if (isLoader) {
          return (
            <Wrapper key={row.key}>
              {repeat(itemsPerRow, (index) => (
                <Skeleton
                  key={index}
                  height={204}
                  style={{ borderRadius: theme.other.spacing._8 }}
                />
              ))}
            </Wrapper>
          );
        }

        const elements = rowItems.map((prop) => (
          <div style={{ width: itemWidth }} key={prop.id}>
            <PlayerCell {...prop} />
          </div>
        ));

        if ((isFetching || isLoading) && elements.length < itemsPerRow) {
          elements.push(
            ...repeat(itemsPerRow - elements.length, (index) => (
              <div style={{ width: itemWidth }} key={index}>
                <Skeleton
                  key={index}
                  height={204}
                  style={{ borderRadius: theme.other.spacing._8 }}
                />
              </div>
            ))
          );
        }

        return <Wrapper key={row.key}>{elements}</Wrapper>;
      })}
    </div>
  );
}

export default BoardPropsList;
